import { assign, merge, uniqueId } from 'lodash/fp';
import * as accountActionsConfig from 'shared/workflows/generic_config/account.json';
import * as caseActionsConfig from 'shared/workflows/generic_config/case.json';
import * as communicateActionsConfig from 'shared/workflows/generic_config/communicate.json';
import * as computeActionsConfig from 'shared/workflows/generic_config/compute.json';
import * as graphActionsConfig from 'shared/workflows/generic_config/graph.json';
import * as inquiryActionsConfig from 'shared/workflows/generic_config/inquiry.json';
import * as inquiryFlowActionsConfig from 'shared/workflows/generic_config/inquiry_flow.json';
import * as listActionsConfig from 'shared/workflows/generic_config/list.json';
import * as otherActionsConfig from 'shared/workflows/generic_config/other.json';
import * as reportActionsConfig from 'shared/workflows/generic_config/report.json';
import * as transactionActionsConfig from 'shared/workflows/generic_config/transaction.json';
import * as verificationActionsConfig from 'shared/workflows/generic_config/verification.json';

import { ActionGroup } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/selectActionUtils';
import { createMockEnvironmentProduction } from 'dashboard/lib/stubs/environment';
import {
  createMockChoicesField,
  createMockHashField,
  createMockIntegerField,
  createMockJSONField,
  createMockMultiChoicesField,
  createMockStringField,
} from 'dashboard/lib/stubs/fields';
import { createMockTrigger } from 'dashboard/lib/stubs/trigger';
import platformObjectJsonSchema from 'dashboard/lib/stubs/workflow/json-schema.json';
import primitiveSchema from 'dashboard/lib/stubs/workflow/primitives.json';
import { createMockUsableObjects } from 'dashboard/lib/stubs/workflow-editor-context';
import {
  ConditionalRule,
  generateJsonSchemaConfig,
  JsonLogicValue,
  LogicBlockOrV2,
  LogicBlockV2,
} from 'dashboard/lib/workflows';
import { BaseTrigger, Trigger } from 'dashboard/models/trigger';
import {
  Workflow,
  WorkflowActionMeta,
  WorkflowAttributes,
  WorkflowFieldKey,
  WorkflowFieldObjectKey,
  WorkflowOperatorKey,
  WorkflowRelationships,
  WorkflowSchema,
  WorkflowStatus,
} from 'dashboard/models/workflow';
import {
  WorkflowRun,
  WorkflowRunAttributes,
  WorkflowRunRelationships,
  WorkflowRunStatus,
  WorkflowRunType,
} from 'dashboard/models/workflow-run';
import {
  GenericWorkflowAction,
  WorkflowCriteriaVariableSchemas,
  WorkflowFilterFieldConfigs,
  WorkflowObjectSchemas,
  WorkflowOperators,
  WorkflowRedactableFieldConfig,
} from 'dashboard/models/workflow-schema';
import {
  AllWorkflowAction,
  HttpPlatformIntegrationConfig,
  WorkflowStep,
  WorkflowStepActionKey,
  WorkflowStepType,
} from 'dashboard/models/workflow-version';
import { ObjectSchemaMap } from 'dashboard/redux/slices/object-schemas';
import { EventName } from 'lib/constants/events';
import { FieldSchema, FieldSchemaType } from 'lib/fields';
import { ApiModel } from 'lib/interfaces/api';
import { JsonSchemaTypes } from 'lib/json-schema';
import { DeepPartial } from 'lib/types';

export const generateWorkflowId = () => uniqueId('wfl_');
export const generateWorkflowVersionId = () => uniqueId('wfv_');
export const generateWorkflowRunId = () => uniqueId('wfr_');

const mockEnvironment = createMockEnvironmentProduction();

export function createMockWorkflow(
  attributes?: Partial<WorkflowAttributes>,
  relationships?: Partial<WorkflowRelationships>,
  id?: string,
): Workflow {
  return {
    attributes: {
      status: WorkflowStatus.Paused,
      active: false,
      name: 'workflow',
      description: 'description',
      version: 1,
      'environment-id': mockEnvironment.id,
      'created-at': '',
      'updated-at': '',
      'trigger-type': 'trigger-event',
      ...attributes,
    },
    id: id || generateWorkflowId(),
    relationships: {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'active-deployment': { data: null },
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'latest-draft-version': { data: null },
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'latest-published-version': { data: null },
      environments: { data: [mockEnvironment] },
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'case-templates': { data: null },
      ...relationships,
    },
    type: ApiModel.Workflow,
  };
}

export function createMockWorkflowV2(
  attributes?: Partial<WorkflowAttributes>,
  relationships?: Partial<WorkflowRelationships>,
  id?: string,
): Workflow {
  return {
    attributes: {
      status: WorkflowStatus.Paused,
      active: false,
      name: '',
      description: 'description',
      version: 2,
      'environment-id': mockEnvironment.id,
      'created-at': '',
      'updated-at': '',
      'trigger-type': 'trigger-event',
      ...attributes,
    },
    id: id || generateWorkflowId(),
    relationships: {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'active-deployment': { data: null },
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'latest-draft-version': { data: null },
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'latest-published-version': { data: null },
      environments: { data: [mockEnvironment] },
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'case-templates': { data: null },
      ...relationships,
    },
    type: ApiModel.Workflow,
  };
}

export function createMockWorkflowRun(
  attributes?: Partial<WorkflowRunAttributes>,
  relationships?: Partial<WorkflowRunRelationships>,
  id?: string,
): WorkflowRun {
  return {
    attributes: {
      status: WorkflowRunStatus.Completed,
      'run-type': WorkflowRunType.Event,
      'created-at': '',
      'updated-at': '',
      'completed-at': '',
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'initial-variables': null,
      output: {},
      ...attributes,
    },
    id: id || generateWorkflowRunId(),
    relationships: {
      workflow: {
        data: {
          type: ApiModel.Workflow,
          id: generateWorkflowId(),
        },
      },
      'workflow-version': {
        data: {
          type: ApiModel.WorkflowVersion,
          id: generateWorkflowVersionId(),
        },
      },
      'trigger-object': undefined,
      steps: {
        data: [],
      },
      creator: undefined,
      'child-workflow-runs': {
        data: [],
      },
      'parent-workflow-run': {
        data: {
          type: ApiModel.WorkflowRun,
          id: generateWorkflowRunId(),
        },
      },
      ...relationships,
    },
    type: ApiModel.WorkflowRun,
  };
}

export const createMockOrGroup = (andGroups: LogicBlockV2[]): LogicBlockOrV2 => {
  return { or: andGroups };
};

export const createMockAndGroup = (rules: ConditionalRule[]): LogicBlockV2 => {
  return { and: rules };
};

export const createMockRule = (
  operatorKey: WorkflowOperatorKey,
  logicKey: string,
  logicValue: JsonLogicValue,
): ConditionalRule => {
  return {
    [operatorKey]: [{ var: logicKey }, logicValue],
  };
};

const REPORT_COMMON_ASSOCIATIONS: WorkflowObjectSchemas['associations'] = [
  { cardinality: 'one', key: 'account', type: WorkflowFieldObjectKey.Account },
  { cardinality: 'one', key: 'inquiry', type: WorkflowFieldObjectKey.Inquiry },
  { cardinality: 'one', key: 'report_template', type: WorkflowFieldObjectKey.ReportTemplate },
  { cardinality: 'one', key: 'environment', type: WorkflowFieldObjectKey.Environment },
];

const VERIFICATION_COMMON_ASSOCIATIONS: WorkflowObjectSchemas['associations'] = [
  { cardinality: 'one', key: 'inquiry', type: WorkflowFieldObjectKey.Inquiry },
];

// Sets the key on the field schema to the WorkflowFieldKey
const setFieldSchemaKeys = (hash: Partial<Record<WorkflowFieldKey, FieldSchema>>) => {
  return Object.entries(hash).reduce<Record<string, FieldSchema>>(
    (newHash, [key, value]) => ({
      ...newHash,
      [key]: { ...value, key },
    }),
    {},
  );
};

/*
  script to re-generate platform object schemas (run on local rails console):

  organization = OrganizationHelper.persona_development
  filter = PlatformObject::Filter.new(organization: organization, include_merged: true)
  schemas = PlatformObject.subtypes.flat_map { |po| po.schema(filter: filter)['oneOf'] }.index_by { |schema| schema['$id'] }
  object_schemas = [
    [PlatformObject::Account, 'acttp_wTnKqFf3YaUtmBcXEunMEwEaeMtS'],
    [PlatformObject::Case, 'ctmpl_ZhpottYRvH9CCr7v8dpfzL4bkhrr'],
    [PlatformObject::Inquiry, 'itmpl_GkxajgMxRWAfc6DyPG614CCDSaNV'],
    [PlatformObject::Inquiry, 'itmpl_7ep6xk6fqovDNqXJDkZ2tkEDZS5f'],
    [PlatformObject::Txn, 'txntp_iBj7aCww66MgCdjtzSgFgRnqSV4y'],
    [PlatformObject::Txn, 'txntp_Jp45Law8ZRMvpSApBdZVWok8PbVX'],
  ].map do |plob, template_id|
    object_filter = PlatformObject::Filter.from_params(organization_id: organization.token, template_id: template_id)
    plob.schema(filter: object_filter)
  end.index_by { |schema| schema['$id'] }
  File.write('front-end/dashboard/lib/stubs/workflow/json-schema.json', JSON.pretty_generate(schemas.merge(object_schemas)))
*/
const plobJsonSchema = platformObjectJsonSchema as ObjectSchemaMap;
const mockSchemasById = {
  '/schema/account/acttp_123': plobJsonSchema['/schema/account/acttp_wTnKqFf3YaUtmBcXEunMEwEaeMtS'],
  '/schema/case/ctmpl_123': plobJsonSchema['/schema/case/ctmpl_ZhpottYRvH9CCr7v8dpfzL4bkhrr'],
  '/schema/inquiry/itmpl_123': plobJsonSchema['/schema/inquiry/itmpl_GkxajgMxRWAfc6DyPG614CCDSaNV'],
  '/schema/inquiry/itmpl_456': plobJsonSchema['/schema/inquiry/itmpl_7ep6xk6fqovDNqXJDkZ2tkEDZS5f'],
  '/schema/transaction/txntp_123':
    plobJsonSchema['/schema/transaction/txntp_iBj7aCww66MgCdjtzSgFgRnqSV4y'],
  '/schema/transaction/txntp_456':
    plobJsonSchema['/schema/transaction/txntp_Jp45Law8ZRMvpSApBdZVWok8PbVX'],
};
export const createMockPlatformObjectJsonSchema = (
  schemasById: ObjectSchemaMap = mockSchemasById,
): ObjectSchemaMap => {
  return merge(platformObjectJsonSchema, schemasById);
};

export const createMockCriteriaVariableSchemas = (
  schema?: WorkflowCriteriaVariableSchemas,
): WorkflowCriteriaVariableSchemas => {
  return {
    [WorkflowFieldObjectKey.Account]: {
      common: setFieldSchemaKeys({
        [WorkflowFieldKey.AnyPriorInquiryStatus]: createMockMultiChoicesField(),
        [WorkflowFieldKey.Id]: createMockStringField(),
        [WorkflowFieldKey.EmailAddress]: createMockStringField(),
        [WorkflowFieldKey.NameFirst]: createMockStringField(),
        [WorkflowFieldKey.NameLast]: createMockStringField(),
        [WorkflowFieldKey.PhoneNumber]: createMockStringField(),
      }),
      'with-template': {},
      associations: [
        { cardinality: 'one', key: 'account_type', type: WorkflowFieldObjectKey.AccountType },
      ],
    },
    [WorkflowFieldObjectKey.Case]: {
      common: setFieldSchemaKeys({
        [WorkflowFieldKey.Id]: createMockStringField(),
      }),
      'with-template': {
        ctmpl_123: setFieldSchemaKeys({
          [WorkflowFieldKey.CaseResolution]: createMockChoicesField({
            config: {
              options: ['Approved', 'Declined'],
            },
          }),
          [WorkflowFieldKey.CaseState]: createMockChoicesField({
            config: {
              options: ['state_1', 'state_2'],
              'option-labels': ['Approved', 'Declined'],
            },
          }),
        }),
      },
      associations: [
        { cardinality: 'one', key: 'account', type: WorkflowFieldObjectKey.Account },
        { cardinality: 'one', key: 'inquiry', type: WorkflowFieldObjectKey.Inquiry },
        { cardinality: 'one', key: 'case_template', type: WorkflowFieldObjectKey.CaseTemplate },
        { cardinality: 'one', key: 'environment', type: WorkflowFieldObjectKey.Environment },
      ],
    },
    [WorkflowFieldObjectKey.Inquiry]: {
      common: setFieldSchemaKeys({
        [WorkflowFieldKey.Age]: createMockIntegerField(),
        [WorkflowFieldKey.BehaviorThreatLevel]: createMockChoicesField({
          config: {
            options: ['low', 'medium', 'high'],
          },
        }),
        [WorkflowFieldKey.BrowserCount]: createMockIntegerField(),
        [WorkflowFieldKey.Country]: createMockChoicesField({
          config: {
            options: ['US', 'CA'],
          },
        }),
        [WorkflowFieldKey.DocumentExtractions]: createMockHashField([
          createMockStringField({ key: 'address_street' }),
          createMockStringField({ key: 'address_city' }),
          createMockStringField({ key: 'address_postal_code' }),
        ]),
        [WorkflowFieldKey.FailedChecks]: createMockMultiChoicesField({
          config: {
            options: ['check_1', 'check_2', 'check_3'],
          },
        }),
        [WorkflowFieldKey.FlowState]: createMockJSONField({
          type: JsonSchemaTypes.Object,
          properties: {},
        }),
        [WorkflowFieldKey.Id]: createMockStringField(),
        [WorkflowFieldKey.LinkUrl]: createMockStringField(),
      }),
      'with-template': {
        itmpl_123: setFieldSchemaKeys({
          [WorkflowFieldKey.Fields]: createMockHashField([
            createMockStringField({ key: 'address_street' }),
            createMockStringField({ key: 'address_city' }),
            createMockStringField({ key: 'address_postal_code' }),
          ]),
        }),
      },
      associations: [
        { cardinality: 'one', key: 'account', type: WorkflowFieldObjectKey.Account },
        { cardinality: 'one', key: 'template', type: WorkflowFieldObjectKey.Template },
        { cardinality: 'one', key: 'environment', type: WorkflowFieldObjectKey.Environment },
        {
          cardinality: 'many',
          key: 'verifications_selfie',
          type: WorkflowFieldObjectKey.VerificationSelfie,
        },
      ],
    },
    [WorkflowFieldObjectKey.Transaction]: {
      common: {},
      'with-template': {
        txntp_123: setFieldSchemaKeys({
          [WorkflowFieldKey.Fields]: createMockHashField([
            createMockStringField({ key: 'address_street' }),
            createMockStringField({ key: 'address_city' }),
            createMockStringField({ key: 'full_address', config: { computed: true } }),
          ]),
          [WorkflowFieldKey.Status]: createMockChoicesField({
            config: {
              options: ['txnst_1', 'txnst_2'],
              'option-labels': ['approved', 'declined'],
            },
          }),
        }),
        txntp_456: setFieldSchemaKeys({
          [WorkflowFieldKey.Fields]: createMockHashField([
            createMockStringField({ key: 'address_postal_code' }),
            createMockStringField({ key: 'address_city' }),
          ]),
          [WorkflowFieldKey.Status]: createMockChoicesField({
            config: {
              options: ['txnst_3', 'txnst_4'],
              'option-labels': ['approved', 'declined'],
            },
          }),
        }),
      },
      associations: [
        {
          cardinality: 'one',
          key: 'transaction_type',
          type: WorkflowFieldObjectKey.TransactionType,
        },
        { cardinality: 'one', key: 'environment', type: WorkflowFieldObjectKey.Environment },
      ],
    },

    [WorkflowFieldObjectKey.CaseTemplate]: {
      common: setFieldSchemaKeys({
        [WorkflowFieldKey.Token]: createMockChoicesField({
          config: {
            options: ['abc123', 'def456'],
          },
        }),
      }),
      'with-template': {},
      associations: [],
    },
    [WorkflowFieldObjectKey.ReportMxAccount]: {
      common: {},
      'with-template': {},
      associations: REPORT_COMMON_ASSOCIATIONS,
    },
    [WorkflowFieldObjectKey.ReportSentilinkScores]: {
      common: {},
      'with-template': {},
      associations: REPORT_COMMON_ASSOCIATIONS,
    },
    [WorkflowFieldObjectKey.ReportCoinbaseCheckCryptoRisk]: {
      common: {},
      'with-template': {},
      associations: REPORT_COMMON_ASSOCIATIONS,
    },
    [WorkflowFieldObjectKey.GraphQuery]: { common: {}, 'with-template': {}, associations: [] },
    [WorkflowFieldObjectKey.ReportAddressLookup]: {
      common: {},
      'with-template': {},
      associations: REPORT_COMMON_ASSOCIATIONS,
    },
    [WorkflowFieldObjectKey.ReportAdverseMedia]: {
      common: {},
      'with-template': {},
      associations: REPORT_COMMON_ASSOCIATIONS,
    },
    [WorkflowFieldObjectKey.ReportClearbitBusinessLookup]: {
      common: {},
      'with-template': {},
      associations: REPORT_COMMON_ASSOCIATIONS,
    },
    [WorkflowFieldObjectKey.ReportEmailAddress]: {
      common: {},
      'with-template': {},
      associations: REPORT_COMMON_ASSOCIATIONS,
    },
    [WorkflowFieldObjectKey.ReportPhoneNumber]: {
      common: {},
      'with-template': {},
      associations: REPORT_COMMON_ASSOCIATIONS,
    },
    [WorkflowFieldObjectKey.ReportScreening]: {
      common: {},
      'with-template': {},
      associations: REPORT_COMMON_ASSOCIATIONS,
    },
    [WorkflowFieldObjectKey.ReportSynthetic]: {
      common: {},
      'with-template': {},
      associations: REPORT_COMMON_ASSOCIATIONS,
    },
    [WorkflowFieldObjectKey.ReportWatchlist]: {
      common: {},
      'with-template': {},
      associations: REPORT_COMMON_ASSOCIATIONS,
    },
    [WorkflowFieldObjectKey.ReportProfile]: {
      common: {},
      'with-template': {},
      associations: REPORT_COMMON_ASSOCIATIONS,
    },
    [WorkflowFieldObjectKey.ReportTemplate]: {
      common: setFieldSchemaKeys({
        [WorkflowFieldKey.Token]: createMockChoicesField({
          config: {
            options: ['rptp_123', 'rptp_456'],
          },
        }),
      }),
      'with-template': {},
      associations: [],
    },
    [WorkflowFieldObjectKey.Template]: {
      common: setFieldSchemaKeys({
        [WorkflowFieldKey.Token]: createMockChoicesField({
          config: {
            options: ['abc123', 'def456'],
          },
        }),
      }),
      'with-template': {},
      associations: [],
    },
    [WorkflowFieldObjectKey.TransactionType]: {
      common: setFieldSchemaKeys({
        [WorkflowFieldKey.Token]: createMockChoicesField({
          config: {
            options: ['abc123', 'def456'],
          },
        }),
      }),
      'with-template': {},
      associations: [],
    },
    [WorkflowFieldObjectKey.VerificationAamva]: {
      common: {},
      'with-template': {},
      associations: VERIFICATION_COMMON_ASSOCIATIONS,
    },
    [WorkflowFieldObjectKey.VerificationCertificateKorea]: {
      common: {},
      'with-template': {},
      associations: VERIFICATION_COMMON_ASSOCIATIONS,
    },
    [WorkflowFieldObjectKey.VerificationDatabase]: {
      common: {},
      'with-template': {},
      associations: VERIFICATION_COMMON_ASSOCIATIONS,
    },
    [WorkflowFieldObjectKey.VerificationDatabaseAadhaar]: {
      common: {},
      'with-template': {},
      associations: VERIFICATION_COMMON_ASSOCIATIONS,
    },
    [WorkflowFieldObjectKey.VerificationDatabaseBusiness]: {
      common: {},
      'with-template': {},
      associations: VERIFICATION_COMMON_ASSOCIATIONS,
    },
    [WorkflowFieldObjectKey.VerificationDatabaseBusinessFootprint]: {
      common: {},
      'with-template': {},
      associations: VERIFICATION_COMMON_ASSOCIATIONS,
    },
    [WorkflowFieldObjectKey.VerificationDatabaseEcbsv]: {
      common: {},
      'with-template': {},
      associations: VERIFICATION_COMMON_ASSOCIATIONS,
    },
    [WorkflowFieldObjectKey.VerificationDatabasePhoneCarrier]: {
      common: {},
      'with-template': {},
      associations: VERIFICATION_COMMON_ASSOCIATIONS,
    },
    [WorkflowFieldObjectKey.VerificationDatabaseTin]: {
      common: {},
      'with-template': {},
      associations: VERIFICATION_COMMON_ASSOCIATIONS,
    },
    [WorkflowFieldObjectKey.VerificationDigitalIdFinnishTrustNetwork]: {
      common: {},
      'with-template': {},
      associations: VERIFICATION_COMMON_ASSOCIATIONS,
    },
    [WorkflowFieldObjectKey.VerificationDigitalIdOneId]: {
      common: {},
      'with-template': {},
      associations: VERIFICATION_COMMON_ASSOCIATIONS,
    },
    [WorkflowFieldObjectKey.VerificationDigitalIdSingpass]: {
      common: {},
      'with-template': {},
      associations: VERIFICATION_COMMON_ASSOCIATIONS,
    },
    [WorkflowFieldObjectKey.VerificationDigitalIdUkSharecode]: {
      common: {},
      'with-template': {},
      associations: VERIFICATION_COMMON_ASSOCIATIONS,
    },
    [WorkflowFieldObjectKey.VerificationDocument]: {
      common: {},
      'with-template': {},
      associations: [
        ...VERIFICATION_COMMON_ASSOCIATIONS,
        {
          cardinality: 'one',
          key: 'document/generic',
          type: WorkflowFieldObjectKey.DocumentGeneric,
        },
      ],
    },
    [WorkflowFieldObjectKey.VerificationEmailAddress]: {
      common: {},
      'with-template': {},
      associations: VERIFICATION_COMMON_ASSOCIATIONS,
    },
    [WorkflowFieldObjectKey.VerificationGovernmentId]: {
      common: setFieldSchemaKeys({ [WorkflowFieldKey.NameFirst]: createMockStringField() }),
      'with-template': {},
      associations: VERIFICATION_COMMON_ASSOCIATIONS,
    },
    [WorkflowFieldObjectKey.VerificationPhoneNumber]: {
      common: {},
      'with-template': {},
      associations: VERIFICATION_COMMON_ASSOCIATIONS,
    },
    [WorkflowFieldObjectKey.VerificationSelfie]: {
      common: {},
      'with-template': {},
      associations: VERIFICATION_COMMON_ASSOCIATIONS,
    },
    ...schema,
  };
};

export const createMockRedactableFieldConfigs = (
  redactableFieldConfigs?: WorkflowRedactableFieldConfig[],
) => {
  return [
    ...(redactableFieldConfigs ?? [
      {
        fields: [
          {
            path: 'shortcut_copies',
            label: 'Shortcut Copies',
            'template-name': 'Blu',
            'template-id': 'tmpl_blu',
          },
          {
            path: 'shortcut_pastes',
            label: 'Shortcut Pastes',
            'template-name': 'Blu',
            'template-id': 'tmpl_blu',
          },
          {
            path: 'verification.*',
            label: 'Verifications',
            'template-name': 'Blu',
            'template-id': 'tmpl_blu',
          },
          {
            path: 'entity_confidence_score',
            label: 'Entity Confidence Scores',
            'template-name': 'Blu',
            'template-id': 'tmpl_blu',
          },
        ],
        token: 'inquiry',
      },
      {
        fields: [
          {
            path: 'fields.first_field',
            label: 'First field',
            'template-name': 'Txn Type I',
            'template-id': 'txn-type-token',
          },
        ],
        token: 'transaction',
      },
    ]),
  ];
};

export const createMockFilterFieldConfigs = (
  filterFieldConfigs?: Partial<WorkflowFilterFieldConfigs>,
): WorkflowFilterFieldConfigs => {
  return { ...filterFieldConfigs } as WorkflowFilterFieldConfigs;
};

export const createMockOperatorsMap = (operators?: WorkflowOperators) => {
  return {
    [FieldSchemaType.String]: [
      WorkflowOperatorKey.Equals,
      WorkflowOperatorKey.DoesNotEqual,
      WorkflowOperatorKey.ContainsAnySubstring,
      WorkflowOperatorKey.ContainsNoSubstring,
      WorkflowOperatorKey.StartsWith,
      WorkflowOperatorKey.In,
      WorkflowOperatorKey.NotIn,
      WorkflowOperatorKey.IsBlank,
      WorkflowOperatorKey.IsNotBlank,
      WorkflowOperatorKey.VersionGreaterThan,
      WorkflowOperatorKey.VersionGreaterThanOrEqual,
      WorkflowOperatorKey.VersionLessThan,
      WorkflowOperatorKey.VersionLessThanOrEqual,
    ],
    [FieldSchemaType.Boolean]: [WorkflowOperatorKey.Equals],
    [FieldSchemaType.Date]: [
      WorkflowOperatorKey.Equals,
      WorkflowOperatorKey.DoesNotEqual,
      WorkflowOperatorKey.GreaterThan,
      WorkflowOperatorKey.GreaterThanOrEqual,
      WorkflowOperatorKey.LessThan,
      WorkflowOperatorKey.LessThanOrEqual,
      WorkflowOperatorKey.In,
      WorkflowOperatorKey.NotIn,
      WorkflowOperatorKey.IsBlank,
      WorkflowOperatorKey.IsNotBlank,
    ],
    [FieldSchemaType.Number]: [
      WorkflowOperatorKey.Equals,
      WorkflowOperatorKey.DoesNotEqual,
      WorkflowOperatorKey.GreaterThan,
      WorkflowOperatorKey.GreaterThanOrEqual,
      WorkflowOperatorKey.LessThan,
      WorkflowOperatorKey.LessThanOrEqual,
      WorkflowOperatorKey.In,
      WorkflowOperatorKey.NotIn,
      WorkflowOperatorKey.IsBlank,
      WorkflowOperatorKey.IsNotBlank,
    ],
    [FieldSchemaType.Integer]: [
      WorkflowOperatorKey.Equals,
      WorkflowOperatorKey.DoesNotEqual,
      WorkflowOperatorKey.GreaterThan,
      WorkflowOperatorKey.GreaterThanOrEqual,
      WorkflowOperatorKey.LessThan,
      WorkflowOperatorKey.LessThanOrEqual,
      WorkflowOperatorKey.In,
      WorkflowOperatorKey.NotIn,
      WorkflowOperatorKey.IsBlank,
      WorkflowOperatorKey.IsNotBlank,
    ],
    [FieldSchemaType.Choices]: [
      WorkflowOperatorKey.Equals,
      WorkflowOperatorKey.DoesNotEqual,
      WorkflowOperatorKey.In,
      WorkflowOperatorKey.NotIn,
      WorkflowOperatorKey.IsBlank,
      WorkflowOperatorKey.IsNotBlank,
    ],
    [FieldSchemaType.MultiChoices]: [
      WorkflowOperatorKey.Contains,
      WorkflowOperatorKey.IncludesAll,
      WorkflowOperatorKey.IncludesNone,
      WorkflowOperatorKey.IncludesNotAll,
      WorkflowOperatorKey.IncludesOnly,
      WorkflowOperatorKey.IncludesExactly,
      WorkflowOperatorKey.IncludesNotExactly,
      WorkflowOperatorKey.IsBlank,
      WorkflowOperatorKey.IsNotBlank,
    ],
    [FieldSchemaType.Array]: [
      WorkflowOperatorKey.Contains,
      WorkflowOperatorKey.IncludesAll,
      WorkflowOperatorKey.IncludesNone,
      WorkflowOperatorKey.IncludesNotAll,
      WorkflowOperatorKey.IncludesOnly,
      WorkflowOperatorKey.IncludesExactly,
      WorkflowOperatorKey.IncludesNotExactly,
      WorkflowOperatorKey.IsBlank,
      WorkflowOperatorKey.IsNotBlank,
    ],
    [FieldSchemaType.File]: [WorkflowOperatorKey.IsBlank, WorkflowOperatorKey.IsNotBlank],
    [FieldSchemaType.Hash]: [WorkflowOperatorKey.IsBlank, WorkflowOperatorKey.IsNotBlank],
    [FieldSchemaType.Relation]: [WorkflowOperatorKey.IsBlank, WorkflowOperatorKey.IsNotBlank],
    ...operators,
  };
};

export const createMockGenericWorkflowActions = (
  workflowActions: GenericWorkflowAction[] = [],
): GenericWorkflowAction[] => {
  return [
    createMockGenericWorkflowAction(),
    createMockGenericWorkflowAction({
      title: 'Find or Create Account',
      key: 'find_or_create_account',
      enabled: true,
      category: ActionGroup.Accounts,
      request: {
        schema: {
          type: 'object',
          required: ['account-type-token', 'reference-id'],
          properties: {
            'account-type-token': { type: 'string' },
            'reference-id': { type: 'string' },
          },
        },
        uiSchema: {
          'account-type-token': {
            'ui:placeholder': 'Select account type...',
            'ui:title': 'Account type',
            'ui:widget': 'select',
            'ui:options': {
              enumType: 'meta',
              enumTypeOptions: ['account-types'],
            },
          },
          'reference-id': {
            'ui:title': 'Reference ID',
            'ui:field': 'handlebars',
            'ui:options': {
              type: 'input',
              allowedFieldTypes: ['string'],
            },
          },
        },
      },
    }),
    createMockGenericWorkflowAction({
      title: 'Update Account Status',
      key: 'update_account_status',
      enabled: true,
      category: ActionGroup.Accounts,
      request: {
        schema: {
          type: 'object',
          required: ['account', 'account-status-id'],
          properties: {
            account: { type: 'string' },
            'account-status-id': { type: 'string' },
          },
        },
        uiSchema: {
          account: {
            'ui:placeholder': 'Select account...',
            'ui:title': 'Account',
            'ui:widget': 'select',
            'ui:options': {
              enumType: 'object',
              enumTypeOptions: ['account'],
            },
          },
          'account-status-id': {
            'ui:placeholder': 'Select account status...',
            'ui:title': 'Account Status',
            'ui:field': 'handlebars',
            'ui:options': {
              enumType: 'meta',
              dependentOn: 'account',
              enumTypOptions: ['account-status'],
            },
          },
        },
      },
    }),
    createMockGenericAddCommentToCaseWorkflowAction(),
    createMockGenericUpdateTransactionStatusWorkflowAction(),
    createMockGenericWorkflowAction({
      title: 'Copy Tags',
      key: 'copy_tags',
      enabled: false,
      category: ActionGroup.Other,
      request: {
        schema: {
          type: 'object',
          required: ['from-object', 'to-object'],
          properties: {
            'from-object': { type: 'string' },
            'to-object': { type: 'string' },
          },
        },
        uiSchema: {
          'from-object': {
            'ui:title': 'From',
          },
          'to-object': {
            'ui:title': 'To',
          },
        },
      },
    }),
    ...workflowActions,
  ];
};

export const createMockWorkflowPrimitives = (
  schemasById: ObjectSchemaMap = {},
): ObjectSchemaMap => {
  return merge(primitiveSchema, schemasById);
};

export const createMockGenericUpdateTransactionStatusWorkflowAction = (): GenericWorkflowAction => {
  return {
    title: 'Update Transaction Status',
    key: 'update_transaction_status',
    enabled: true,
    category: ActionGroup.Transaction,
    request: {
      schema: {
        type: 'object',
        required: ['transaction', 'transaction-status-token'],
        properties: {
          transaction: { type: 'string' },
          'transaction-status-token': { type: 'string' },
        },
      },
      uiSchema: {
        transaction: {
          'ui:placeholder': 'Select transaction...',
          'ui:title': 'Transaction',
          'ui:widget': 'select',
          'ui:options': {
            enumType: 'object',
            enumTypeOptions: ['transaction'],
          },
        },
        'transaction-status-token': {
          'ui:placeholder': 'Select transaction status...',
          'ui:title': 'Status',
          'ui:widget': 'select',
          'ui:options': {
            enumType: 'meta',
            enumTypeOptions: ['transaction-statuses'],
            dependentOn: 'transaction',
          },
        },
      },
    },
  };
};

export const createMockGenericFindOrCreateAccountWorkflowAction = (): GenericWorkflowAction => {
  return {
    title: 'Find or Create Account',
    key: 'find_or_create_account',
    enabled: true,
    category: ActionGroup.Accounts,
    request: {
      schema: {
        type: 'object',
        required: ['account-type-token'],
        properties: {
          'account-type-token': { type: 'string' },
          'reference-id': { type: 'string' },
        },
      },
      uiSchema: {
        'account-type-token': {
          'ui:placeholder': 'Select account type...',
          'ui:title': 'Account type',
          'ui:widget': 'select',
          'ui:options': {
            enumType: 'meta',
            enumTypeOptions: ['account-types'],
            templateObjectType: 'account',
          },
        },
        'reference-id': {
          'ui:title': 'Reference ID',
          'ui:field': 'handlebars',
          'ui:options': {
            type: 'input',
            allowedFieldTypes: ['string'],
          },
        },
      },
    },
  };
};

export const createMockGenericAttachObjectsToCaseWorkflowAction = (): GenericWorkflowAction => {
  return {
    title: 'Attach Objects to Case',
    key: 'attach_objects_to_case',
    enabled: true,
    category: ActionGroup.Case,
    request: {
      schema: {
        type: 'object',
        required: ['case', 'case-objects'],
        properties: {
          case: {
            type: 'string',
          },
          'case-objects': {
            type: 'array',
            items: {
              type: 'string',
            },
            minItems: 1,
          },
        },
      },
      uiSchema: {
        case: {
          'ui:placeholder': 'Select case...',
          'ui:title': 'Case',
          'ui:widget': 'select',
          'ui:options': {
            enumType: 'object',
            enumTypeOptions: ['case'],
          },
        },
        'case-objects': {
          'ui:title': 'Case objects',
          'ui:widget': 'select',
          'ui:options': {
            enumType: 'object',
            enumTypeOptions: ['graph-query'],
          },
        },
      },
    },
  };
};

export const createMockGenericAddCommentToCaseWorkflowAction = (): GenericWorkflowAction => {
  return {
    title: 'Add Comment to Case',
    key: 'add_comment_to_case',
    enabled: true,
    category: ActionGroup.Case,
    request: {
      schema: {
        type: 'object',
        required: ['case', 'comment'],
        properties: {
          case: { type: 'string' },
          comment: { type: 'string' },
        },
      },
      uiSchema: {
        case: {
          'ui:placeholder': 'Select case...',
          'ui:title': 'Case',
          'ui:widget': 'select',
          'ui:options': {
            enumType: 'object',
            enumTypeOptions: ['case'],
          },
        },
        comment: {
          'ui:title': 'Comment',
          'ui:field': 'handlebars',
          'ui:options': {
            type: 'text-area',
            allowedFieldTypes: ['boolean', 'integer', 'number', 'string', 'date', 'enum'],
          },
        },
      },
    },
  };
};

export const createMockGenericCreateCaseWorkflowAction = (): GenericWorkflowAction => {
  return {
    title: 'Create Case',
    key: 'create_case',
    enabled: false,
    category: ActionGroup.Case,
    request: {
      schema: {
        type: 'object',
        required: ['case-template-token', 'case-fields'],
        properties: {
          'case-template-token': {
            type: 'string',
          },
          'case-fields': {
            type: 'object',
          },
        },
      },
      uiSchema: {
        'case-template-token': {
          'ui:placeholder': 'Select case template...',
          'ui:title': 'Case template',
          'ui:widget': 'select',
          'ui:options': {
            enumType: 'meta',
            enumTypeOptions: ['case-templates'],
          },
        },
        'case-fields': {
          'ui:title': 'Case fields',
          'ui:field': 'fieldsMapping',
          'ui:options': {
            type: 'schemas-on-template',
            dependentOn: 'case-template-token',
          },
        },
      },
    },
  };
};

export const createMockGenericWorkflowAction = (
  schema?: Partial<GenericWorkflowAction>,
): GenericWorkflowAction =>
  assign(
    {
      title: 'Add Case to Queue',
      key: 'add_case_to_case_queue',
      enabled: true,
      category: ActionGroup.Case,
      request: {
        schema: {
          type: 'object',
          required: ['case', 'case-queue-token'],
          properties: {
            case: { type: 'string' },
            'case-queue-token': { type: 'string' },
          },
        },
        uiSchema: {
          case: {
            'ui:placeholder': 'Select case...',
            'ui:title': 'Case',
            'ui:help': 'If case already has an assignee, the user will be unassigned',
            'ui:widget': 'select',
            'ui:options': {
              enumType: 'object',
              enumTypeOptions: ['case'],
            },
          },
          'case-queue-token': {
            'ui:placeholder': 'Select case queue...',
            'ui:title': 'Case queue',
            'ui:widget': 'select',
            'ui:options': {
              enumType: 'meta',
              enumTypeOptions: ['case-queues'],
            },
          },
        },
      },
    },
    schema,
  );

export const createMockGenericWorkflowActionWithConditionalDependencies = (
  schema?: Partial<GenericWorkflowAction>,
): GenericWorkflowAction =>
  assign(
    {
      title: 'Send Email',
      key: 'send_email',
      enabled: true,
      category: ActionGroup.Communicate,
      request: {
        schema: {
          type: 'object',
          properties: {
            'smtp-server': {
              type: 'string',
              oneOf: [
                {
                  const: 'smtp-1',
                  title: 'Smtp 1',
                },
                {
                  const: 'smtp-2',
                  title: 'Smtp 2',
                },
              ],
            },
          },
          required: ['smtp-server'],
          dependencies: {
            'smtp-server': {
              oneOf: [
                {
                  properties: {
                    'smtp-server': {
                      oneOf: [
                        {
                          const: 'smtp-1',
                        },
                      ],
                    },
                    'smtp-1-field-1': {
                      type: 'string',
                    },
                  },
                  required: ['smtp-server', 'smtp-1-field-1'],
                },
                {
                  properties: {
                    'smtp-server': {
                      oneOf: [
                        {
                          const: 'smtp-2',
                        },
                      ],
                    },
                    'smtp-2-field-1': {
                      type: 'string',
                    },
                  },
                  required: ['smtp-server', 'smtp-2-field-1'],
                },
              ],
            },
          },
        },
        uiSchema: {
          'smtp-server': {
            'ui:placeholder': 'Select an SMTP option...',
            'ui:title': 'SMTP Server',
            'ui:widget': 'select',
          },
          'smtp-2-field-1': {
            'ui:title': 'SMTP 2 Field 1',
          },
          'smtp-2-field-2': {
            'ui:title': 'SMTP 2 Field 2',
          },
        },
      },
    },
    schema,
  );

export const createMockHttpPlatformIntegrationConfig = (
  schema?: DeepPartial<HttpPlatformIntegrationConfig>,
): HttpPlatformIntegrationConfig => {
  const defaultConfig: HttpPlatformIntegrationConfig = {
    title: 'Have A Lot of Fun',
    enabled: true,
    category: ActionGroup.Marketplace,
    key: WorkflowStepActionKey.RunHttpPlatformIntegration,
    request: {
      parametersSchema: {
        type: 'object',
        properties: {
          'super-sweet-user-id': {
            type: 'string',
          },
        },
      },
      parametersUiSchema: {
        'super-sweet-user-id': {
          'ui:title': 'User ID',
        },
      },
      schema: {
        type: 'object',
        properties: {
          'very-fun-name': {
            type: 'string',
          },
        },
      },
      uiSchema: {},
    },
    integration: {
      vendor: 'mastercard-identity',
      operation_id: 'accountOpening',
    },
    'integration-configs': [
      {
        label: 'Account Opening API Key',
        value: 'eic_tzrrmL57aKKkvSDhJwwy5wyfcH8X',
      },
    ],
    response: {
      schema: {},
      uiSchema: {},
    },
  };

  return merge(defaultConfig, schema);
};

/**
 * This mock method is used to create a schema that is real and corresponds to what we expect to see
 * in real production environments so our storybook stories can render and be usable.
 */
export const createMockWorkflowSchemaForStorybook = (): WorkflowSchema => {
  return {
    // TODO: Replace this with a fixture that is more representative of real
    'criteria-variable-schemas': createMockCriteriaVariableSchemas(),
    // TODO: Replace this with a fixture that is more representative of real
    operators: createMockOperatorsMap(),
    // TODO: Replace this with a fixture that is more representative of real
    // TODO: Replace this with a fixture that is more representative of real
    'filter-field-configs': createMockFilterFieldConfigs(),
    // TODO: Replace this with a fixture that is more representative of real
    'generic-workflow-actions': [
      ...accountActionsConfig.actions,
      ...caseActionsConfig.actions,
      ...communicateActionsConfig.actions,
      ...computeActionsConfig.actions,
      ...graphActionsConfig.actions,
      ...inquiryActionsConfig.actions,
      ...inquiryFlowActionsConfig.actions,
      ...listActionsConfig.actions,
      ...otherActionsConfig.actions,
      ...reportActionsConfig.actions,
      ...transactionActionsConfig.actions,
      ...verificationActionsConfig.actions,
    ] as GenericWorkflowAction[],
    primitives: createMockWorkflowPrimitives(),
  };
};

export const createMockWorkflowSchema = (schema?: Partial<WorkflowSchema>): WorkflowSchema => {
  return {
    'criteria-variable-schemas': createMockCriteriaVariableSchemas(
      schema?.['criteria-variable-schemas'],
    ),
    operators: createMockOperatorsMap(schema?.operators),
    'filter-field-configs': createMockFilterFieldConfigs(schema?.['filter-field-configs']),
    'generic-workflow-actions': createMockGenericWorkflowActions(
      schema?.['generic-workflow-actions'],
    ),
    primitives: createMockWorkflowPrimitives(schema?.primitives),
  };
};

export const createMockCasePath = (steps?: WorkflowStep[]): WorkflowStep[] => {
  return [
    {
      token: 'my_token_1',
      type: WorkflowStepType.Action,
      name: 'Create Case',
      action: {
        key: WorkflowStepActionKey.CreateCase,
        version: 1,
        params: {
          'case-objects': [BaseTrigger],
          'case-template-token': 'ctmpl_123',
          'case-fields': {},
        },
      },
    },
    {
      type: WorkflowStepType.Wait,
      token: 'my_token_2',
      name: 'my-name',
      'watched-events': [
        {
          'event-name': EventName.CaseAssigned,
          'event-object-step-token': 'my_token_1',
        },
      ],
    },
    ...(steps || []),
  ];
};

type WorkflowActionMap = {
  [K in AllWorkflowAction['key']]: Extract<AllWorkflowAction, { key: K }>;
};

export const createMockWorkflowAction = <K extends AllWorkflowAction['key']>({
  key,
  params,
  version,
}: {
  key: K;
  params?: WorkflowActionMap[K]['params'];
  version?: WorkflowActionMap[K]['version'];
}): WorkflowActionMap[K] => {
  return {
    key: key,
    params: params || {},
    version: version || 1,
  } as WorkflowActionMap[K];
};

export const createMockActionMeta = ({
  'account-types': accountTypes,
  'case-queues': caseQueues,
  'case-states': caseStates,
  'case-templates': caseTemplates,
  graph: graph,
  'inquiry-templates': inquiryTemplates,
  'report-templates': reportTemplates,
  templates: templates,
  'transaction-statuses': transactionStatuses,
  'transaction-types': transactionTypes,
  'verification-templates': verificationTemplates,
}: Partial<WorkflowActionMeta> = {}): WorkflowActionMeta => {
  return {
    'account-identifiers': [],
    'account-identifiers-unique': [],
    'account-statuses': [],
    'account-types': accountTypes || [],
    'case-states': caseStates || [],
    'case-templates': caseTemplates || [],
    'case-queues': caseQueues || [],
    'external-integrations': [],
    graph: graph || null,
    'graph-query-templates': [],
    'inquiry-templates': inquiryTemplates || [],
    'inquiry-template-version': {},
    lists: [],
    'report-templates': reportTemplates || [],
    [ApiModel.ReportTemplateEquifaxOneview]: [],
    [ApiModel.ReportTemplateKyckrBusinessLookup]: [],
    [ApiModel.ReportTemplateMiddeskBusinessLookup]: [],
    [ApiModel.ReportTemplateSentilinkApplicationRisk]: [],
    [ApiModel.ReportTemplateSentilinkScores]: [],
    [ApiModel.ReportTemplateChainalysisAddressScreening]: [],
    [ApiModel.ReportTemplateMxAccount]: [],
    [ApiModel.ReportTemplateTrmWalletScreening]: [],
    tags: [],
    templates: templates || [],
    themes: [],
    'theme-sets': [],
    'transaction-identifiers': [],
    'transaction-identifiers-unique': [],
    'transaction-statuses': transactionStatuses || [],
    'transaction-types': transactionTypes || [],
    users: [],
    'verification-templates': verificationTemplates || [],
    'verification-template-versions': [],
    workflows: [],
  };
};

export const createMockWorkflowContext = ({
  trigger = createMockTrigger(),
  workflowSchema = createMockWorkflowSchema(),
  platformObjectJsonSchema = createMockPlatformObjectJsonSchema(),
  activeStep,
  stepsConfig = [],
}: {
  trigger?: Trigger;
  workflowSchema?: WorkflowSchema;
  platformObjectJsonSchema?: ObjectSchemaMap;
  activeStep?: WorkflowStep;
  stepsConfig?: WorkflowStep[];
} = {}) => {
  const usableObjects = createMockUsableObjects({
    trigger: trigger,
    stepsToProcess: stepsConfig,
    activeStepToken: activeStep?.token,
    workflowSchema,
    platformObjectJsonSchema,
  });
  const { jsonSchema, tokensToDisplayValue } = generateJsonSchemaConfig({
    usableObjects,
  });
  return {
    jsonSchema,
    tokensToDisplayValue,
    usableObjects,
    workflowSchema,
    trigger,
  };
};
